<template>
    <div
        class="context_menu_item"
        :class="{
            'with-submenu': hasSubmenu,
            'disabled': disabled,
            'current-project': highlighted
        }"
        @click.stop="handleClick"
        @mouseenter="openSubmenu"
        @mouseleave="closeSubmenu"
        @focusin="openSubmenu"
        @focusout="handleFocusOut"
        ref="itemRef"
    >
        <i v-if="icon" :class="['context_menu_icon', icon]"></i>
        <slot></slot>
        <i v-if="hasSubmenu" :class="submenuArrowClass"></i>
        <div
            v-if="hasSubmenu && isSubmenuOpen"
            class="context_submenu"
            :style="submenuStyle"
            ref="submenuRef"
        >
            <slot name="submenu"></slot>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ContextMenuItem',
    props: {
        icon: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        highlighted: {
            type: Boolean,
            default: false
        }
    },
    emits: ['click'],
    data() {
        return {
            isSubmenuOpen: false,
            isSubmenuPositioned: false,
            submenuPlacement: 'right',
            submenuTop: -5,
            submenuMaxHeight: 320,
            submenuMaxWidth: 320,
            submenuUpdateFrame: null,
            submenuListenersActive: false
        }
    },
    computed: {
        hasSubmenu() {
            return !!this.$slots.submenu;
        },
        submenuStyle() {
            const horizontalPosition = this.submenuPlacement === 'left'
                ? { left: 'auto', right: '100%' }
                : { left: '100%', right: 'auto' };

            return {
                ...horizontalPosition,
                top: this.submenuTop + 'px',
                maxHeight: this.submenuMaxHeight + 'px',
                maxWidth: this.submenuMaxWidth + 'px',
                minWidth: Math.min(208, this.submenuMaxWidth) + 'px',
                visibility: this.isSubmenuPositioned ? 'visible' : 'hidden'
            };
        },
        submenuArrowClass() {
            return [
                'bi',
                this.isSubmenuOpen && this.submenuPlacement === 'left' ? 'bi-chevron-left' : 'bi-chevron-right',
                'submenu-arrow'
            ];
        }
    },
    beforeUnmount() {
        this.removeSubmenuListeners();
        this.cancelSubmenuPositionUpdate();
    },
    methods: {
        handleClick(event) {
            if (this.disabled) {
                event.preventDefault();
                return;
            }
            if (this.hasSubmenu) {
                this.openSubmenu();
                return;
            }
            this.$emit('click', event);
        },
        openSubmenu() {
            if (!this.hasSubmenu || this.disabled) return;

            if (!this.isSubmenuOpen) {
                this.isSubmenuOpen = true;
                this.isSubmenuPositioned = false;
                this.$nextTick(this.updateSubmenuPosition);
            } else {
                this.queueSubmenuPositionUpdate();
            }
            this.addSubmenuListeners();
        },
        closeSubmenu() {
            if (!this.hasSubmenu) return;

            this.isSubmenuOpen = false;
            this.isSubmenuPositioned = false;
            this.removeSubmenuListeners();
            this.cancelSubmenuPositionUpdate();
        },
        handleFocusOut(event) {
            if (!this.hasSubmenu) return;
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.closeSubmenu();
            }
        },
        async updateSubmenuPosition() {
            const item = this.$refs.itemRef;
            const submenu = this.$refs.submenuRef;
            if (!item || !submenu) return;

            const viewportMargin = 8;
            const preferredMaxHeight = 320;
            const preferredMaxWidth = 320;
            const minimumReadableSize = 120;
            const defaultMinWidth = 208;
            const availableViewportHeight = Math.max(minimumReadableSize, window.innerHeight - viewportMargin * 2);

            this.submenuMaxHeight = Math.min(preferredMaxHeight, availableViewportHeight);

            await this.$nextTick();
            if (!this.isSubmenuOpen || !this.$refs.itemRef || !this.$refs.submenuRef) return;

            const itemRect = this.$refs.itemRef.getBoundingClientRect();
            const rightSpace = window.innerWidth - itemRect.right - viewportMargin;
            const leftSpace = itemRect.left - viewportMargin;
            const hasRoomRight = rightSpace >= defaultMinWidth;
            const hasRoomLeft = leftSpace >= defaultMinWidth;

            this.submenuPlacement = hasRoomRight || (!hasRoomLeft && rightSpace >= leftSpace)
                ? 'right'
                : 'left';

            const sideSpace = this.submenuPlacement === 'right' ? rightSpace : leftSpace;
            this.submenuMaxWidth = Math.max(
                minimumReadableSize,
                Math.min(preferredMaxWidth, sideSpace)
            );

            await this.$nextTick();
            if (!this.isSubmenuOpen || !this.$refs.itemRef || !this.$refs.submenuRef) return;

            const measuredItemRect = this.$refs.itemRef.getBoundingClientRect();
            const submenuRect = this.$refs.submenuRef.getBoundingClientRect();
            const submenuHeight = Math.min(submenuRect.height, this.submenuMaxHeight);
            const preferredViewportTop = measuredItemRect.top - 5;
            const maxViewportTop = Math.max(
                viewportMargin,
                window.innerHeight - viewportMargin - submenuHeight
            );
            const clampedViewportTop = Math.min(
                Math.max(preferredViewportTop, viewportMargin),
                maxViewportTop
            );

            this.submenuTop = Math.round(clampedViewportTop - measuredItemRect.top);
            this.isSubmenuPositioned = true;
        },
        queueSubmenuPositionUpdate() {
            if (!this.isSubmenuOpen || !this.hasSubmenu) return;
            if (this.submenuUpdateFrame) return;

            this.submenuUpdateFrame = window.requestAnimationFrame(() => {
                this.submenuUpdateFrame = null;
                this.updateSubmenuPosition();
            });
        },
        cancelSubmenuPositionUpdate() {
            if (!this.submenuUpdateFrame) return;
            window.cancelAnimationFrame(this.submenuUpdateFrame);
            this.submenuUpdateFrame = null;
        },
        addSubmenuListeners() {
            if (this.submenuListenersActive) return;
            window.addEventListener('scroll', this.queueSubmenuPositionUpdate, true);
            window.addEventListener('resize', this.queueSubmenuPositionUpdate);
            this.submenuListenersActive = true;
        },
        removeSubmenuListeners() {
            if (!this.submenuListenersActive) return;
            window.removeEventListener('scroll', this.queueSubmenuPositionUpdate, true);
            window.removeEventListener('resize', this.queueSubmenuPositionUpdate);
            this.submenuListenersActive = false;
        }
    }
}
</script>

<style scoped>
.context_menu_item {
    padding-inline: 0.75rem;
    font-weight: 600;
    padding-block: 0.4rem;
    border-radius: 0.75rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    transition: background-color 240ms ease;
}

.context_menu_item:hover {
    background-color: #393939;
}

.context_menu_item.disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.context_menu_item.disabled:hover {
    background-color: transparent;
}

.context_menu_item.current-project {
    background-color: rgba(47, 47, 47, 0.5);
}

.context_menu_icon {
    color: #a3a3a3;
    margin-right: 0.25rem;
}

.with-submenu {
    position: relative;
    overflow: visible;
}

.submenu-arrow {
    margin-left: auto;
    color: #a3a3a3;
}

.context_submenu {
    position: absolute;
    left: 100%;
    top: -5px;
    background: rgb(47, 47, 47);
    border: 1px solid rgba(47, 47, 47, 1);
    border-radius: 0.75rem;
    padding: 0.25rem;
    z-index: 1001;
    min-width: 13rem;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    box-sizing: border-box;
    scrollbar-width: thin;
    backdrop-filter: blur(12.3px);
    -webkit-backdrop-filter: blur(12.3px);
}

.context_submenu :deep(.context_menu_item) {
    min-width: 0;
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
}
</style>
